import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { MeteredPaywallSubscribeModal, MeteredPaywallUsageBanner, PaywallElement } from '@shared/dream-components';

import { usePostDataContext } from '~/context/usePostDataContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import useGatedSubscribe from '~/hooks/useGatedSubscribe';
import type { AnyPaywall } from '~/interfaces/paywall';
import { isMeteredPaywall, toPaywallStyleProps, toUsageStyleProps } from '~/interfaces/paywall';

interface Props {
  paywall: AnyPaywall | null;
  showPaywallAndHidePostContent: boolean;
  showMeteredUsageBanner: boolean;
  isPaywallBreakInView: boolean;
  paywallUrl?: string;
  loginPath: string;
}

const PostContentPaywall = ({
  paywall,
  showPaywallAndHidePostContent,
  showMeteredUsageBanner,
  isPaywallBreakInView,
  paywallUrl,
  loginPath,
}: Props) => {
  const { handleSubscribe, referralId } = useGatedSubscribe({
    sendOtpAfterSubscribe: !!paywall && isMeteredPaywall(paywall),
  });
  const { setIsMeteredPaywallModalOpen } = usePostPageContext() ?? {};
  const { postMetaInformation } = usePostDataContext();
  // Only true after the client loader's fetchPostMetaInformation resolves with
  // personalized (user-session-aware) data. Prevents flashing the metered paywall
  // modal during hydration when the CDN-cached server response may carry stale values.
  const isPostMetaFetched = !!postMetaInformation?._fetched;
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const meteredModalProps = (premiumOfferId?: string) => ({
    onSubscribe: handleSubscribe,
    upgradePath: '/upgrade',
    premiumOfferId,
    referralId,
    loginPath,
  });

  // Type-narrow the paywall to its specific variant once so the case flags and
  // render helpers below don't need repeated type guards.
  let meteredPaywall = null;
  if (paywall && isMeteredPaywall(paywall)) meteredPaywall = paywall;

  let standardPaywall = null;
  if (paywall && !isMeteredPaywall(paywall)) standardPaywall = paywall;

  // ── Case logic ──
  // Show the metered subscribe modal when the whole post is gated OR when the
  // reader has scrolled to an inline paywall break.
  const showMeteredModal =
    (showPaywallAndHidePostContent || isPaywallBreakInView) && !!meteredPaywall && isPostMetaFetched;
  // Show the standard paywall widget when the post is hidden and the publication
  // returns a standard paywall (no metered paywall configured, or limit exceeded).
  const showStandardPaywall = showPaywallAndHidePostContent && !!standardPaywall && isPostMetaFetched;
  // Show the usage banner while the reader still has free views remaining.
  const showUsageBanner = showMeteredUsageBanner && !isBannerDismissed;

  useEffect(() => {
    setIsMeteredPaywallModalOpen?.(showMeteredModal);
    return () => setIsMeteredPaywallModalOpen?.(false);
  }, [showMeteredModal, setIsMeteredPaywallModalOpen]);

  // ── Render helpers ──
  const renderMeteredModal = () => {
    if (!meteredPaywall) return null;
    return createPortal(
      <>
        <div className="fixed inset-0 z-[47] bg-black/60" />
        <div className="fixed inset-0 z-[47] flex items-center justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto">
            <MeteredPaywallSubscribeModal
              styles={toPaywallStyleProps(meteredPaywall.paywall_styles, meteredPaywall.logo_url)}
              copy={{ ...meteredPaywall.paywall_copy, showTosConsent: meteredPaywall.show_tos_consent }}
              isInline
              {...meteredModalProps(meteredPaywall.premium_offer_id)}
            />
          </div>
        </div>
      </>,
      document.body,
    );
  };

  const renderStandardPaywall = () => {
    if (!standardPaywall) return null;
    return (
      <PaywallElement
        theme={standardPaywall.theme}
        upgradeUrl={paywallUrl}
        shouldUseUrl
        upgradePath="/upgrade"
        isLive
        element={{
          type: 'paywall_break',
          attrs: {
            id: standardPaywall.id || 'post-page-paywall',
            data: {
              title: standardPaywall.title,
              description: standardPaywall.description,
              cta_text: standardPaywall.cta_text,
              benefits: standardPaywall.benefits,
              subheader: standardPaywall.subheader,
              image_url: standardPaywall.image_url,
              style: standardPaywall.style,
            },
          },
          content: [{ id: 'text', text: '' }],
        }}
      >
        {null}
      </PaywallElement>
    );
  };

  const renderUsageBanner = () => {
    if (!meteredPaywall) return null;
    // Portal to document.body so the banner's z-index operates in the root stacking context,
    // preventing any ancestor transform/stacking context from hiding it behind the footer.
    return createPortal(
      <MeteredPaywallUsageBanner
        {...toUsageStyleProps(meteredPaywall.usage_styles)}
        {...meteredPaywall.usage_copy}
        viewedCount={postMetaInformation?.metered_viewed_count ?? undefined}
        upgradePath="/upgrade"
        premiumOfferId={meteredPaywall.premium_offer_id}
        onClose={() => setIsBannerDismissed(true)}
      />,
      document.body,
    );
  };

  // ── Routing ──
  if (showMeteredModal) return renderMeteredModal();
  if (showStandardPaywall) return renderStandardPaywall();
  if (showUsageBanner) return renderUsageBanner();
  return null;
};

export { PostContentPaywall };
